import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { 
  useGetBannerQuery, 
  useCreateBannerMutation, 
  useUpdateBannerMutation,
  useGetCategoriesQuery,
  useGetExhibitionsQuery,
  useGetArticlesQuery,
  useGetActivitiesQuery
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';
import { getImageUrl } from '../../../utils/imageHelpers';

const AdminBannerForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetBannerQuery(id!, { skip: !isEdit });
  const { data: categoriesData } = useGetCategoriesQuery({});
  const { data: exhibitionsData } = useGetExhibitionsQuery({});
  const { data: articlesData } = useGetArticlesQuery({});
  const { data: activitiesData } = useGetActivitiesQuery({});
  const [createBanner, { isLoading: isCreating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    link_to: 'no',
    link_id: '',
    link_url: '',
    sort_order: '',
    image: null as File | null,
  });

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const banner = data.data;
      const linkTo = banner.link_to || 'no';
      setFormData({
        title: banner.title || '',
        title_ar: banner.title_ar || '',
        link_to: linkTo,
        link_id: ['category', 'exhibition', 'activity', 'article'].includes(linkTo) ? (banner.link_id?.toString() || '') : '',
        link_url: linkTo === 'url' ? (banner.link_id || '') : '',
        sort_order: banner.sort_order?.toString() || '0',
        image: null,
      });
      if (banner.image) {
        const imageFilename = banner.image.includes('/uploads/') 
          ? banner.image.split('/uploads/')[1] 
          : banner.image;
        setExistingImage(imageFilename);
      } else {
        setExistingImage(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'link_to') {
      // Reset link fields when link_to changes
      setFormData(prev => ({ 
        ...prev, 
        link_to: value,
        link_id: '',
        link_url: ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
    setFormData(prev => ({ ...prev, image: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.link_to) {
      showError(t('Please select a link type.'));
      return;
    }

    if (formData.link_to !== 'no' && formData.link_to !== 'url') {
      if (!formData.link_id) {
        showError(t('Please select a link option.'));
        return;
      }
    }

    if (formData.link_to === 'url' && !formData.link_url) {
      showError(t('Please enter an external URL.'));
      return;
    }

    if (!isEdit && !formData.image && !existingImage) {
      showError(t('Please upload a banner image.'));
      return;
    }
    
    try {
      const submitData: any = {
        title: formData.title.trim(),
        title_ar: formData.title_ar.trim(),
        link_to: formData.link_to,
        sort_order: parseInt(formData.sort_order) || 0,
      };

      if (formData.link_to === 'url') {
        submitData.link_url = formData.link_url.trim();
      } else if (formData.link_to !== 'no') {
        submitData.link_id = parseInt(formData.link_id);
      }

      if (formData.image) {
        submitData.image = formData.image;
      }

      if (isEdit) {
        const result = await updateBanner({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Banner updated successfully!'));
      } else {
        const result = await createBanner(submitData).unwrap();
        showSuccess(result.message || t('Banner created successfully!'));
      }
      
      navigate('/admin/banners');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || t(isEdit ? 'Failed to update banner. Please try again.' : 'Failed to create banner. Please try again.'));
      }
    }
  };

  const categories = categoriesData?.data?.data || [];
  const exhibitions = exhibitionsData?.data?.data || [];
  const articles = articlesData?.data?.data || [];
  const activities = activitiesData?.data?.data || [];

  // Format options for react-select
  const categoryOptions = useMemo(() => 
    categories.map((cat: any) => ({
      value: cat.id,
      label: cat.name || cat.title || `Category ${cat.id}`,
    })), [categories]
  );

  const exhibitionOptions = useMemo(() => 
    exhibitions.map((exh: any) => ({
      value: exh.id,
      label: exh.title || `Exhibition ${exh.id}`,
    })), [exhibitions]
  );

  const activityOptions = useMemo(() => 
    activities.map((act: any) => ({
      value: act.id,
      label: act.title || `Activity ${act.id}`,
    })), [activities]
  );

  const articleOptions = useMemo(() => 
    articles.map((art: any) => ({
      value: art.id,
      label: art.title || `Article ${art.id}`,
    })), [articles]
  );

  // Get current options based on link_to
  const getCurrentOptions = () => {
    switch (formData.link_to) {
      case 'category':
        return categoryOptions;
      case 'exhibition':
        return exhibitionOptions;
      case 'activity':
        return activityOptions;
      case 'article':
        return articleOptions;
      default:
        return [];
    }
  };

  // Get selected value for react-select
  const getSelectedValue = () => {
    if (!formData.link_id) return null;
    const options = getCurrentOptions();
    return options.find((opt: any) => opt.value.toString() === formData.link_id.toString()) || null;
  };

  const handleLinkIdChange = (selectedOption: any) => {
    setFormData(prev => ({
      ...prev,
      link_id: selectedOption ? selectedOption.value.toString() : '',
    }));
  };

  const showLinkId = formData.link_to && ['category', 'exhibition', 'activity', 'article'].includes(formData.link_to);
  const showLinkUrl = formData.link_to === 'url';
  const showLinkField = formData.link_to && formData.link_to !== 'no';

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Banner') : t('Add New Banner')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/banners'); }} className="text-muted text-hover-primary">{t('Banners')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Banner Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Banner Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Link To')}</label>
                      <select
                        className="form-select form-control-solid"
                        name="link_to"
                        value={formData.link_to}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="category">{t('Category')}</option>
                        <option value="exhibition">{t('Exhibition')}</option>
                        <option value="activity">{t('Activity')}</option>
                        <option value="article">{t('Article')}</option>
                        <option value="url">{t('External URL')}</option>
                        <option value="no">{t('No Link')}</option>
                      </select>
                    </div>

                    {showLinkId && (
                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">
                          {formData.link_to === 'category' && t('Select Category')}
                          {formData.link_to === 'exhibition' && t('Select Exhibition')}
                          {formData.link_to === 'activity' && t('Select Activity')}
                          {formData.link_to === 'article' && t('Select Article')}
                        </label>
                        <Select
                          isSearchable
                          options={getCurrentOptions()}
                          value={getSelectedValue()}
                          onChange={handleLinkIdChange}
                          placeholder={t('Search and select...')}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '48px',
                              borderColor: '#e4e6ef',
                              '&:hover': {
                                borderColor: '#e4e6ef',
                              },
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: '#a1a5b7',
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    )}

                    {showLinkUrl && (
                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">{t('External URL')}</label>
                        <input
                          type="url"
                          className="form-control form-control-solid"
                          placeholder={t('Enter External URL')}
                          name="link_url"
                          value={formData.link_url}
                          onChange={handleInputChange}
                          required={showLinkUrl}
                        />
                      </div>
                    )}

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Sort Order')}</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Sort Order (0, 1, 2...)')}
                        name="sort_order"
                        value={formData.sort_order}
                        onChange={handleInputChange}
                        min="0"
                      />
                      <div className="form-text">{t('Lower numbers appear first. Default: 0')}</div>
                    </div>

                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className={`fs-6 fw-semibold mb-2 ${!isEdit ? 'required' : ''}`}>{t('Banner Image')}</label>
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        accept=".png, .jpg, .jpeg"
                        label={t('Upload Banner Image')}
                        name="image"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/banners')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBannerForm;

